import type { ColumnDef } from "@tanstack/react-table"
import { createFileRoute } from "@tanstack/react-router"
import { ArrowUpDown, Edit, MoreHorizontal, Trash2 } from "lucide-react"
import { useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DataTable,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/datatable"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { PageHeader } from "@/components/ui/PageHeader"

export const Route = createFileRoute("/design/table-showcase")({
  component: TableShowcase,
})

// Sample data types
type Product = {
  id: string
  name: string
  category: string
  price: number
  stock: number
  status: "available" | "low" | "out"
}

// Sample data
const products: Product[] = [
  {
    id: "1",
    name: "Melk halfvolle",
    category: "Zuivel",
    price: 1.29,
    stock: 150,
    status: "available",
  },
  {
    id: "2",
    name: "Brood wit",
    category: "Bakkerij",
    price: 2.49,
    stock: 25,
    status: "low",
  },
  {
    id: "3",
    name: "Kaas belegen",
    category: "Zuivel",
    price: 4.99,
    stock: 0,
    status: "out",
  },
  {
    id: "4",
    name: "Appels Jonagold",
    category: "Groente & Fruit",
    price: 2.99,
    stock: 80,
    status: "available",
  },
  {
    id: "5",
    name: "Pasta penne",
    category: "Droogwaren",
    price: 1.89,
    stock: 200,
    status: "available",
  },
  {
    id: "6",
    name: "Yoghurt naturel",
    category: "Zuivel",
    price: 1.79,
    stock: 45,
    status: "available",
  },
  {
    id: "7",
    name: "Tomaten",
    category: "Groente & Fruit",
    price: 3.49,
    stock: 12,
    status: "low",
  },
  {
    id: "8",
    name: "Rijst basmati",
    category: "Droogwaren",
    price: 2.99,
    stock: 0,
    status: "out",
  },
]

// Column definitions for DataTable
const columns: Array<ColumnDef<Product>> = [
  {
    accessorKey: "name",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Product
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
  },
  {
    accessorKey: "category",
    header: "Category",
    cell: ({ row }) => <span className="text-neutral-700">{row.getValue("category")}</span>,
  },
  {
    accessorKey: "price",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Price
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const price = parseFloat(row.getValue("price"))
      const formatted = new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
      }).format(price)
      return <div className="font-medium">{formatted}</div>
    },
  },
  {
    accessorKey: "stock",
    header: "Stock",
    cell: ({ row }) => {
      const stock = row.getValue<number>("stock")
      return (
        <div className={stock === 0 ? "text-red-600" : stock < 50 ? "text-orange-600" : ""}>
          {stock}
        </div>
      )
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status")
      return (
        <Badge
          variant={
            status === "available" ? "success" : status === "low" ? "warning" : "destructive"
          }
        >
          {status === "available" ? "Available" : status === "low" ? "Low Stock" : "Out of Stock"}
        </Badge>
      )
    },
  },
  {
    id: "actions",
    cell: () => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem className="text-red-600">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  },
]

function TableShowcase() {
  const [theme, setTheme] = useState<"light" | "dark">("light")

  return (
    <div className={theme === "dark" ? "dark" : ""}>
      <div className="bg-background min-h-screen p-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <div>
            <PageHeader
              title="Table Component Showcase"
              description="Examples of the Shelfi table component system with borderless design"
            />
            <div className="mt-4 flex gap-2">
              <Button
                variant={theme === "light" ? "default" : "secondary"}
                onClick={() => setTheme("light")}
              >
                Light Mode
              </Button>
              <Button
                variant={theme === "dark" ? "default" : "secondary"}
                onClick={() => setTheme("dark")}
              >
                Dark Mode
              </Button>
            </div>
          </div>

          {/* Component Explanation */}
          <section className="space-y-4 rounded-lg bg-blue-50 p-6 dark:bg-blue-950/20">
            <div>
              <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 text-xl font-bold">
                📚 Table Component System
              </h2>
              <p className="mt-2 text-neutral-700 dark:text-neutral-300">
                Shelfi provides a comprehensive table system in{" "}
                <code className="rounded bg-neutral-100 px-1.5 py-0.5 text-sm dark:bg-neutral-800">
                  table.tsx
                </code>{" "}
                with two approaches:
              </p>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="rounded-lg bg-white p-4 dark:bg-neutral-800">
                  <h3 className="text-brand-darkblue-500 dark:text-brand-cyan-150 font-semibold">
                    Basic Table Components
                  </h3>
                  <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                    For simple, static tables or when you need full control. Use individual
                    components: Table, TableRow, TableCell, TableHead, etc.
                  </p>
                </div>
                <div className="rounded-lg bg-white p-4 dark:bg-neutral-800">
                  <h3 className="text-brand-darkblue-500 dark:text-brand-cyan-150 font-semibold">
                    DataTable Component
                  </h3>
                  <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                    For complex tables with sorting, filtering, pagination, and search. A complete
                    solution built on TanStack Table.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Basic Table */}
          <section className="space-y-4">
            <div>
              <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 text-2xl font-bold">
                Basic Table Component
              </h2>
              <p className="mt-1 text-neutral-700 dark:text-neutral-300">
                Use for simple, static data display with manual control over behavior
              </p>
            </div>
            <div className="bg-card overflow-hidden rounded-lg">
              <Table>
                <TableCaption>A list of specifications from the Figma design</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kleur licht</TableHead>
                    <TableHead>Rood, Wit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Materiaal</TableCell>
                    <TableCell>Silicone</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Aantal benodige batterijen</TableCell>
                    <TableCell>4</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Batterij code</TableCell>
                    <TableCell>CR2032</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Bewegingsgevoeligheid</TableCell>
                    <TableCell>Klembevestiging</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Handleiding</TableCell>
                    <TableCell>Bekijk handleiding</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </section>

          {/* Table with Mixed Content */}
          <section className="space-y-4">
            <div>
              <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 text-2xl font-bold">
                Basic Table with Mixed Content
              </h2>
              <p className="mt-1 text-neutral-700 dark:text-neutral-300">
                Still using basic components but with badges, alignment, and richer content
              </p>
            </div>
            <div className="bg-card overflow-hidden rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Type lichtklem</TableCell>
                    <TableCell>LED lamp</TableCell>
                    <TableCell className="text-right">150</TableCell>
                    <TableCell>
                      <Badge variant="success">Available</Badge>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Type voeding</TableCell>
                    <TableCell>Batterij</TableCell>
                    <TableCell className="text-right">25</TableCell>
                    <TableCell>
                      <Badge variant="warning">Low Stock</Badge>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Artikelnummer</TableCell>
                    <TableCell>2371062</TableCell>
                    <TableCell className="text-right">0</TableCell>
                    <TableCell>
                      <Badge variant="destructive">Out of Stock</Badge>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </section>

          {/* Advanced DataTable */}
          <section className="space-y-4">
            <div>
              <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 text-2xl font-bold">
                DataTable Component
              </h2>
              <p className="mt-1 text-neutral-700 dark:text-neutral-300">
                Complete solution with sorting, filtering, pagination, and search out of the box
              </p>
            </div>
            <div className="bg-card overflow-hidden rounded-lg">
              <DataTable columns={columns} data={products} searchPlaceholder="Search products..." />
            </div>
          </section>

          {/* Empty State Example */}
          <section className="space-y-4">
            <div>
              <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 text-2xl font-bold">
                Empty State
              </h2>
              <p className="mt-1 text-neutral-700 dark:text-neutral-300">
                How tables look when there&apos;s no data
              </p>
            </div>
            <div className="bg-card overflow-hidden rounded-lg">
              <DataTable columns={columns} data={[]} searchPlaceholder="Search products..." />
            </div>
          </section>

          {/* Design Specifications */}
          <section className="space-y-4">
            <div>
              <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 text-2xl font-bold">
                Design Specifications
              </h2>
              <p className="mt-1 text-neutral-700 dark:text-neutral-300">
                Key design decisions and specifications
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="bg-card space-y-4 rounded-lg p-6">
                <h3 className="text-lg font-semibold">Spacing</h3>
                <ul className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <li>• Row height: 56px (14 * 4)</li>
                  <li>• Cell padding: 24px horizontal</li>
                  <li>• First/last cell padding: 32px</li>
                  <li>• No borders or dividers</li>
                </ul>
              </div>
              <div className="bg-card space-y-4 rounded-lg p-6">
                <h3 className="text-lg font-semibold">Colors</h3>
                <ul className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <li>• Header background: neutral-50</li>
                  <li>• Header text: neutral-700</li>
                  <li>• Body text: neutral-900</li>
                  <li>• Hover state: neutral-50</li>
                </ul>
              </div>
              <div className="bg-card space-y-4 rounded-lg p-6">
                <h3 className="text-lg font-semibold">Typography</h3>
                <ul className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <li>• Header: text-sm, font-medium</li>
                  <li>• Body: text-sm, font-normal</li>
                  <li>• Caption: text-sm, text-neutral-500</li>
                  <li>• Consistent left alignment</li>
                </ul>
              </div>
              <div className="bg-card space-y-4 rounded-lg p-6">
                <h3 className="text-lg font-semibold">Features</h3>
                <ul className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <li>• TanStack Table integration</li>
                  <li>• Sorting & filtering</li>
                  <li>• Row selection</li>
                  <li>• Pagination</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
